"use client";

import React from "react";
import { Calendar, Cpu, Rocket } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: "01",
  title: "Strategic Audit",
  description: "We conduct a comprehensive audit of your current operations to identify high-leverage automation opportunities within the Automated Growth Systems ecosystem.",
  icon: Calendar
}, {
  id: "02",
  title: "Architecture & Build",
  description: "Our engineers construct a bespoke AGS environment, integrating advanced CRM pipelines and custom-tailored communication matrices.",
  icon: Cpu
}, {
  id: "03",
  title: "Scale & Automate",
  description: "Deploy your intelligent infrastructure. We activate AI-driven protocols to handle engagement and scaling with zero manual friction.",
  icon: Rocket
}];
function TimelineItem({
  step,
  index
}: {
  step: typeof steps[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="116634" ref={ref} data-index={index} className={`relative flex items-center justify-between md:justify-normal w-full mb-24 last:mb-0 ${isEven ? "md:flex-row" : "md:flex-row-reverse"}`}>
      {}
      <div className="absolute left-8 md:left-1/2 w-10 h-10 -translate-x-1/2 flex items-center justify-center z-20">
        <div className={`relative w-5 h-5 rounded-full border-2 transition-all duration-700 flex items-center justify-center ${isVisible ? "border-primary bg-primary shadow-[0_0_20px_var(--color-primary)]" : "border-muted bg-background"}`}>
          {isVisible && <div className="absolute inset-0 rounded-full bg-secondary animate-ping opacity-75" />}
        </div>
      </div>

      {}
      <div className={`w-full pl-24 md:pl-0 md:w-[calc(50%-4rem)] ${isEven ? "md:pr-16 md:text-right" : "md:pl-16 md:text-left"}`}>
        <Card className={`bg-card/40 backdrop-blur-xl border-white/10 shadow-[0_0_20px_rgba(45,127,249,0.1)] transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0 shadow-[0_0_25px_var(--color-secondary)] border-secondary/50" : "opacity-0 translate-y-12"}`}>
          <CardHeader className="gap-4">
            <div className={`flex items-center gap-4 ${isEven ? "md:flex-row-reverse md:justify-start" : "md:justify-start"}`}>
              <span className="font-default text-5xl font-bold text-primary/90 drop-shadow-[0_0_10px_var(--color-primary)]">
                {step.id}
              </span>
              <div className="p-3 rounded-lg bg-secondary/10 border border-secondary/30 shadow-[0_0_10px_var(--color-secondary)]">
                <step.icon className="w-6 h-6 text-secondary" />
              </div>
            </div>
            <div>
              <CardTitle className="text-2xl text-foreground font-default tracking-wide mb-2">
                {step.title}
              </CardTitle>
              <CardDescription className="text-muted-foreground text-base leading-relaxed font-default">
                {step.description}
              </CardDescription>
            </div>
          </CardHeader>
        </Card>
      </div>
    </div>;
}
export default function TheEvolutionPipeline() {
  return <section id="growth-roadmap" className="dark bg-background text-foreground py-32 relative overflow-hidden">
      {}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-secondary/5 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-24">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 tracking-tighter uppercase font-default">
            Growth{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary drop-shadow-[0_0_15px_var(--color-primary)]">
              Roadmap
            </span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg md:text-xl font-default">
            A high-performance blueprint to transition your business into a self-optimizing SaaS powerhouse with AGS Suite.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="absolute left-8 md:left-1/2 top-4 bottom-4 w-0.5 bg-secondary/30 shadow-[0_0_15px_var(--color-secondary)] md:-translate-x-1/2 z-0" />

          {}
          <div className="relative py-8">
            {steps.map((step, index) => <TimelineItem key={step.id} step={step} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}